'use client';

import {
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from '@heroui/react';
import { AlertTriangle, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Modal } from '@/components/ui/modal';
import type { ConfirmVariant } from '@/types/confirm.types';

export interface ConfirmProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title?: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	variant?: ConfirmVariant;
	isLoading?: boolean;
}

/**
 * HeroUI 기반 확인 다이얼로그 컴포넌트
 *
 * @example
 * ```tsx
 * <Confirm
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={() => {
 *     // 확인 버튼 클릭 시 실행할 로직
 *     setIsOpen(false);
 *   }}
 *   title="변경 사항 저장"
 *   message="저장하지 않은 변경 사항이 있습니다. 페이지를 떠나시겠습니까?"
 *   confirmText="나가기"
 *   cancelText="취소"
 *   variant="warning"
 * />
 * ```
 */
export function Confirm({
	isOpen,
	onClose,
	onConfirm,
	title = '확인',
	message,
	confirmText = '확인',
	cancelText = '취소',
	variant = 'info',
	isLoading = false,
}: ConfirmProps) {
	const handleConfirm = () => {
		if (!isLoading) {
			onConfirm();
		}
	};

	const handleClose = () => {
		if (!isLoading) {
			onClose();
		}
	};

	const getVariantStyles = () => {
		switch (variant) {
			case 'warning':
				return {
					icon: <AlertTriangle className="w-6 h-6 text-warning" />,
					confirmVariant: 'primary' as const,
				};
			case 'danger':
				return {
					icon: <AlertTriangle className="w-6 h-6 text-danger" />,
					confirmVariant: 'destructive' as const,
				};
			case 'info':
			default:
				return {
					icon: null,
					confirmVariant: 'primary' as const,
				};
		}
	};

	const styles = getVariantStyles();

	return (
		<Modal
			isOpen={isOpen}
			onOpenChange={handleClose}
			size="md"
			placement="center"
			scrollBehavior="inside"
			hideCloseButton
			classNames={{
				base: "bg-white rounded-lg shadow-xl",
				backdrop: "bg-black/50 backdrop-opacity-40",
				wrapper: "z-50 items-center justify-center",
			}}
		>
			<ModalContent className="bg-white rounded-lg shadow-xl p-0 max-h-[90vh] w-full flex flex-col">
				{(onClose) => (
					<>
						<ModalHeader className="flex items-center justify-between gap-4 pb-4 border-b border-gray-200 px-6 pt-6 flex-shrink-0">
							<div className="flex items-center gap-3 flex-1">
								{styles.icon && (
									<div className="flex-shrink-0">{styles.icon}</div>
								)}
								<h3 className="text-lg font-semibold text-gray-900">{title}</h3>
							</div>
							<button
								type="button"
								onClick={onClose}
								className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
								aria-label="닫기"
								disabled={isLoading}
							>
								<X className="w-5 h-5" />
							</button>
						</ModalHeader>
						<ModalBody className="pt-4 pb-4 px-6">
							<p className="text-gray-700 whitespace-pre-line leading-relaxed">
								{message}
							</p>
						</ModalBody>
						<ModalFooter className="pt-4 border-t border-gray-200 px-6 pb-6 flex-shrink-0">
							<Button variant="ghost" onPress={onClose} isDisabled={isLoading}>
								{cancelText}
							</Button>
							<Button
								variant={styles.confirmVariant}
								onPress={handleConfirm}
								isLoading={isLoading}
							>
								{confirmText}
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}
